// What a picture is, from its own bytes: the format and the size in pixels,
// read from the header the way a browser would, so a file named .jpg that is
// really something else is never kept. Only raster formats a browser shows
// without running anything: no SVG, which can carry script. No AVIF either:
// its size sits in boxes a file can repeat and move, and a browser may decode
// a track the header never mentions, so its size cannot be read for certain.

export type ImageType = "image/jpeg" | "image/png" | "image/webp";

export interface ImageInfo {
  type: ImageType;
  ext: "jpg" | "png" | "webp";
  w: number;
  h: number;
}

const u16 = (b: Uint8Array, i: number) => (b[i] << 8) | b[i + 1];
const u16le = (b: Uint8Array, i: number) => b[i] | (b[i + 1] << 8);
const u24le = (b: Uint8Array, i: number) => b[i] | (b[i + 1] << 8) | (b[i + 2] << 16);
const u32 = (b: Uint8Array, i: number) => ((b[i] << 24) >>> 0) + ((b[i + 1] << 16) | (b[i + 2] << 8) | b[i + 3]);
const ascii = (b: Uint8Array, i: number, n: number) => String.fromCharCode(...b.subarray(i, i + n));

function jpeg(b: Uint8Array): ImageInfo | null {
  let i = 2;
  while (i + 9 < b.length) {
    if (b[i] !== 0xff) return null;
    let m = b[i + 1];
    // fill bytes before a marker
    while (m === 0xff && i + 2 < b.length) {
      i++;
      m = b[i + 1];
    }
    // markers that stand alone, with no length
    if (m === 0x01 || (m >= 0xd0 && m <= 0xd9)) {
      i += 2;
      continue;
    }
    // a start of frame names the size: baseline, progressive, and the rest, never DHT, JPG or DAC
    if (m >= 0xc0 && m <= 0xcf && m !== 0xc4 && m !== 0xc8 && m !== 0xcc) {
      const h = u16(b, i + 5);
      const w = u16(b, i + 7);
      return w && h ? { type: "image/jpeg", ext: "jpg", w, h } : null;
    }
    i += 2 + u16(b, i + 2);
  }
  return null;
}

function webp(b: Uint8Array): ImageInfo | null {
  const chunk = ascii(b, 12, 4);
  let w = 0;
  let h = 0;
  if (chunk === "VP8 " && b.length >= 30) {
    w = u16le(b, 26) & 0x3fff;
    h = u16le(b, 28) & 0x3fff;
  } else if (chunk === "VP8L" && b.length >= 25) {
    w = 1 + (((b[22] & 0x3f) << 8) | b[21]);
    h = 1 + (((b[24] & 0x0f) << 10) | (b[23] << 2) | ((b[22] & 0xc0) >> 6));
  } else if (chunk === "VP8X" && b.length >= 30) {
    w = 1 + u24le(b, 24);
    h = 1 + u24le(b, 27);
  }
  return w && h ? { type: "image/webp", ext: "webp", w, h } : null;
}

/** The picture's format and size, or null when the bytes are not a JPEG, PNG or WebP. */
export function sniffImage(b: Uint8Array): ImageInfo | null {
  if (b.length < 32) return null;
  if (b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return jpeg(b);
  if (b[0] === 0x89 && ascii(b, 1, 3) === "PNG" && ascii(b, 12, 4) === "IHDR") {
    const w = u32(b, 16);
    const h = u32(b, 20);
    return w && h ? { type: "image/png", ext: "png", w, h } : null;
  }
  if (ascii(b, 0, 4) === "RIFF" && ascii(b, 8, 4) === "WEBP") return webp(b);
  return null;
}

/** A page's own picture, as it names it for link previews: og:image, then twitter:image, then image_src. */
export function pageImage(html: string, base: string): string | null {
  const head = html.slice(0, 200_000);
  const tags = head.match(/<(meta|link)\b[^>]*>/gi) ?? [];
  const attr = (tag: string, name: string) => new RegExp(`\\b${name}\\s*=\\s*("([^"]*)"|'([^']*)'|([^\\s>]+))`, "i").exec(tag);
  const value = (tag: string, name: string) => {
    const m = attr(tag, name);
    return m ? (m[2] ?? m[3] ?? m[4] ?? "").trim() : null;
  };
  const find = (test: (tag: string) => boolean, from: string) => {
    for (const t of tags) if (test(t)) return value(t, from);
    return null;
  };
  const named = (key: string) => (t: string) => [value(t, "property"), value(t, "name")].some((v) => v?.toLowerCase() === key);
  const raw =
    find(named("og:image:secure_url"), "content") ??
    find(named("og:image"), "content") ??
    find(named("og:image:url"), "content") ??
    find(named("twitter:image"), "content") ??
    find(named("twitter:image:src"), "content") ??
    find((t) => /^link$/i.test(/^<(\w+)/.exec(t)?.[1] ?? "") && value(t, "rel")?.toLowerCase() === "image_src", "href");
  if (!raw) return null;
  try {
    const u = new URL(raw.replace(/&amp;/g, "&"), base);
    return u.protocol === "https:" || u.protocol === "http:" ? u.toString() : null;
  } catch {
    return null;
  }
}

/** What a background must be: wide, landscape and large enough to fill a screen. */
export function backgroundProblem(i: ImageInfo): string | null {
  // a small file can declare a vast picture, and every browser showing it would have to unpack it
  if (i.w > 4096 || i.h > 4096) return `it is ${i.w}×${i.h}, larger than a screen needs: use a copy at most 4096 wide (3840 fills a 4K screen)`;
  if (i.w < 1280 || i.h < 600) return `it is ${i.w}×${i.h}, too small to fill a screen: find one at least 1920 wide`;
  if (i.w / i.h < 1.2) return `it is ${i.w}×${i.h}, taller than a screen is wide: a background needs a landscape picture`;
  if (i.w / i.h > 3.6) return `it is ${i.w}×${i.h}, a panorama too wide to fill a screen without cropping most of it`;
  return null;
}

/** The address a kept picture is served at: its own content's hash, so the same picture is kept once. */
export const IMAGE_KEY = /^bg\/[a-f0-9]{40}\.(jpg|png|webp)$/;

// ---------------------------------------------------------------------------
// Pictures in a doc board's pages: the same formats as a background, and a GIF too (still or moving, a browser
// shows it without running anything). Never an SVG. Kept under pic/, apart from the backgrounds.

export type PictureType = ImageType | "image/gif";

export interface PictureInfo {
  type: PictureType;
  ext: "jpg" | "png" | "webp" | "gif";
  w: number;
  h: number;
}

/** A page picture's format and size, from its own bytes, or null when they are not a PNG, JPEG, WebP or GIF. */
export function sniffPicture(b: Uint8Array): PictureInfo | null {
  if (b.length >= 32 && (ascii(b, 0, 6) === "GIF87a" || ascii(b, 0, 6) === "GIF89a")) {
    const w = u16le(b, 6);
    const h = u16le(b, 8);
    return w && h ? { type: "image/gif", ext: "gif", w, h } : null;
  }
  return sniffImage(b);
}

/** Whether these bytes are an SVG drawing (or start like one), to say why it is not kept. */
export function looksLikeSvg(b: Uint8Array): boolean {
  const head = new TextDecoder().decode(b.subarray(0, 2048)).replace(/^﻿/, "");
  return /^\s*(<\?xml[^>]*>\s*)?(<!--[\s\S]*?-->\s*|<!doctype[^>]*>\s*)*<svg[\s>]/i.test(head);
}

/** The most a page's picture may be, on a side and in all: past that every browser showing it has to unpack a vast picture. */
export const MAX_PICTURE_SIDE = 12_000;
export const MAX_PICTURE_PIXELS = 60_000_000;

/** Why a picture cannot go in a page, as a sentence, or null. */
export function pictureProblem(i: PictureInfo): string | null {
  if (i.w > MAX_PICTURE_SIDE || i.h > MAX_PICTURE_SIDE || i.w * i.h > MAX_PICTURE_PIXELS) return `That picture is ${i.w}×${i.h} pixels, more than a page can show. Use a copy at most ${MAX_PICTURE_SIDE.toLocaleString("en-US")} pixels on a side.`;
  return null;
}

/**
 * The most pixels on a side an agent is shown a picture at (Round 7): Anthropic's API turns a bigger one away, and a
 * chat holding one has every later message turned away too. The board cannot make a smaller copy, so it says so.
 */
export const MAX_LOOK_SIDE = 8000;

/** Why an agent cannot be shown this picture, as a sentence, or null when it can be (a size that cannot be read is let through, as before). */
export function tooBigToLook(i: { w: number; h: number } | null): string | null {
  if (!i || (i.w <= MAX_LOOK_SIDE && i.h <= MAX_LOOK_SIDE)) return null;
  return `That picture is ${i.w}×${i.h} pixels, too big for an agent to look at (${MAX_LOOK_SIDE.toLocaleString("en-US")} on a side at most), so it was not sent. The user sees it on the board.`;
}

/** A page picture's key: its bytes' hash, salted per workspace, so its address cannot be worked out from the picture. */
export const PICTURE_KEY = /^pic\/[a-f0-9]{40}\.(jpg|png|webp|gif)$/;

/** Where the board serves a kept picture, from its own address. */
export function picturePath(key: string): string {
  return `/api/img/${key}`;
}

/** The kept picture an address names, when it is this board's own (a path, or the board's origin and a path): its key. */
export function pictureKeyOf(href: string, origin?: string | null): string | null {
  const m = /^(https?:\/\/[^/?#]+)?\/api\/img\/(pic\/[a-f0-9]{40}\.(?:jpg|png|webp|gif))(?:[?#].*)?$/i.exec(href.trim());
  if (!m) return null;
  if (m[1] && (!origin || m[1].toLowerCase() !== origin.toLowerCase())) return null;
  return m[2].toLowerCase();
}

/** A picture a deal's note or a mind map idea holds: one the board keeps, by its key, and its size (Features 2, 21 Oct). */
export interface PictureRef {
  key: string;
  w: number;
  h: number;
}

/** A picture reference as the board keeps it, or null: only a key the board's own picture store makes. */
export function cleanPictureRef(raw: unknown): PictureRef | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.key !== "string" || !PICTURE_KEY.test(r.key)) return null;
  const side = (v: unknown) => (typeof v === "number" && Number.isFinite(v) && v >= 0 && v <= 20_000 ? Math.round(v) : 0);
  return { key: r.key, w: side(r.w), h: side(r.h) };
}
